import { useModal } from 'modals';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useSnapshot } from 'valtio';

import {
  hasPremiumFeatures,
  isPro,
  isUnlimited,
} from '@cartwhl/common/stores/userStore';
import { StripeCustomer, StripeProduct } from '@cartwhl/common/types';
import FormattedDate from '@cartwhl/ui/atoms/FormattedDate';
import Text from '@cartwhl/ui/base/Text';
import ArrowIcon from '@cartwhl/ui/icons/ArrowIcon';
import DiscordIcon from '@cartwhl/ui/icons/DiscordIcon';
import MailIcon from '@cartwhl/ui/icons/MailIcon';
import Button from '@cartwhl/ui/input/Button';

import {
  checkoutStore,
  customerStore,
  productStore,
  userStore,
} from 'stores/global';

export default function CurrentPlan() {
  const navigate = useNavigate();
  const { open } = useModal();
  const { products, loading: productsLoading } = useSnapshot(
    productStore.state,
  );
  const { customer, loading: customerLoading } = useSnapshot(
    customerStore.state,
  );
  const { organizationInfo, userInfo } = useSnapshot(userStore.state);

  const nextMonthlyRefreshAt = new Date(userInfo.lastMonthlyRefreshAt);
  nextMonthlyRefreshAt.setMonth(nextMonthlyRefreshAt.getMonth() + 1);

  const subscriberPremiumStatus = customerStore.getSubscriberPremiumStatus(
    customer as any as StripeCustomer,
  );

  const product =
    isUnlimited() && subscriberPremiumStatus !== 'expired'
      ? products.unlimited
      : isPro() && subscriberPremiumStatus !== 'expired'
        ? products.pro
        : products.free;

  const hasActiveSubscription =
    subscriberPremiumStatus === 'isPaying' &&
    userInfo.premiumAccessUntil &&
    userInfo.premiumAccessUntil > new Date().toJSON();

  let price = null;
  if (product) {
    price = productStore.getPriceByProductAndMode(
      (product as StripeProduct).id,
      'month',
    );
  }

  const spinsButtonStyle = hasActiveSubscription
    ? 'bg-gray-90 dark:bg-gray-90 hover:bg-gray-75 dark:hover:bg-gray-75 active:bg-gray-60 dark:active:bg-gray-60 text-white dark:text-white'
    : 'bg-gray-45/35 dark:bg-gray-45/35 hover:bg-gray-60/35 dark:hover:bg-gray-60/35 active:bg-gray-75/35 dark:active:bg-gray-75/35 dark:text-gray-90';

  useEffect(() => {
    productStore.fetchProducts();
  }, []);

  useEffect(() => {
    if (userInfo.stripeCustomerID) {
      customerStore.fetchCustomer(userInfo.stripeCustomerID);
    }
  }, [userInfo.stripeCustomerID]);

  let currentPlan = <></>;
  if (organizationInfo?.organizationName) {
    currentPlan = (
      <span className="flex flex-col">
        {organizationInfo.organizationName}
        <Text variant="studio/body/sm">
          ({organizationInfo.accessLevel === 'EDIT' ? 'Admin' : 'Member'} /{' '}
          {userInfo.planType === 'unlimited'
            ? 'Unlimited'
            : userInfo.planType === 'pro'
              ? 'Professional'
              : 'Free'}
          )
        </Text>
      </span>
    );
  } else if (userInfo.planType === 'unlimited') {
    currentPlan = <span>Unlimited</span>;
  } else if (userInfo.planType === 'pro') {
    currentPlan = <span>Professional</span>;
  } else if (hasPremiumFeatures()) {
    currentPlan = <span>Premium</span>;
  } else {
    currentPlan = <span>Creator</span>;
  }

  const mailtoAddress =
    userInfo.planType === 'free'
      ? 'support@getcartwheel.com'
      : 'pros@getcartwheel.com';

  if (productsLoading || customerLoading) {
    return (
      <div className="bg-yellow text-gray-90 rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 w-32 rounded bg-gray-200" />
          <div className="mt-4 h-6 w-48 rounded bg-gray-200" />
          <div className="mt-4 h-4 w-24 rounded bg-gray-200" />
          <div className="mt-4 h-6 w-32 rounded bg-gray-200" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="bg-yellow text-gray-90 rounded-lg p-6">
        <div className="flex flex-row gap-4">
          <div className="flex grow flex-col gap-4">
            <div>
              <Text variant="studio/body/md" className="opacity-60">
                {subscriberPremiumStatus === 'willExpire'
                  ? 'Expiring plan'
                  : 'Current plan'}
              </Text>
              <p>
                <Text className="font-display" variant="studio/title/lg">
                  {currentPlan}
                </Text>
              </p>
            </div>
            <div className="mt-auto">
              <div className="flex items-center gap-1">
                <Text variant="studio/body/md" className="opacity-60">
                  You currently have
                </Text>
              </div>
              <p>
                <Text variant="studio/display/xl">
                  {userInfo.planType === 'unlimited'
                    ? '∞'
                    : userInfo.spinsAvailable}{' '}
                  Spins
                  <Button
                    variant="secondary"
                    className="bg-gray-45/35 dark:bg-gray-45/35 hover:bg-gray-60/35 dark:hover:bg-gray-60/35 active:bg-gray-75/35 dark:active:bg-gray-75/35 ml-2"
                    size="sm"
                    onClick={() => open('EXPLAIN_SPINS')}
                    icon={<ArrowIcon className="text-gray-90 -rotate-45" />}
                  />
                </Text>
              </p>
            </div>
          </div>
          <div className="flex grow flex-col gap-4">
            <div>
              <Text variant="studio/body/md" className="opacity-60">
                {subscriberPremiumStatus === 'willExpire'
                  ? 'Expires on:'
                  : 'Next billing cycle:'}
              </Text>
              <p>
                {isPro() ? (
                  <FormattedDate date={userInfo.premiumAccessUntil} />
                ) : (
                  <FormattedDate date={userInfo.nextMonthlyRefreshEligibleAt} />
                )}
              </p>
            </div>
            <div className="mt-auto flex flex-col gap-4 sm:flex-row">
              <div className="flex-1">
                <Button
                  className={`${spinsButtonStyle} w-full`}
                  variant="tertiary"
                  onClick={() => open('PURCHASE_SPINS')}
                >
                  Buy more Spins
                </Button>
              </div>
              {!hasActiveSubscription && (
                <div className="flex-1">
                  <Button
                    variant="tertiary"
                    className="bg-gray-90 dark:bg-gray-90 hover:bg-gray-75 dark:hover:bg-gray-75 active:bg-gray-60 dark:active:bg-gray-60 w-full text-white dark:text-white"
                    onClick={() => {
                      const price = productStore.getPriceByProductAndMode(
                        products.pro?.id ?? '',
                        'month',
                      );
                      if (price) {
                        checkoutStore.select(price);
                        checkoutStore.startCheckout();
                        return;
                      }
                      navigate('/pricing');
                    }}
                  >
                    {subscriberPremiumStatus === 'willExpire'
                      ? 'Resubscribe'
                      : 'Upgrade to Pro'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex w-full flex-row gap-2">
        <a
          className="w-full"
          href={`mailto:${mailtoAddress}?subject=Help%20with%20Account%20Status`}
          style={{ textDecoration: 'none' }}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button
            icon={<MailIcon />}
            variant="secondary"
            size="lg"
            className="w-full"
          >
            Contact support
          </Button>
        </a>
        <a
          className="w-full"
          href="https://discord.gg/QGRBqwJakb"
          style={{ textDecoration: 'none' }}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button
            icon={<DiscordIcon />}
            variant="secondary"
            size="lg"
            className="w-full"
          >
            Get help on Discord
          </Button>
        </a>
      </div>
    </div>
  );
}
